import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Layers, Ruler, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";

const HERO =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2400&q=85";

const benefits = [
  { icon: Zap, title: "Efficienza energetica", desc: "Classe A, isolamento termoacustico, pannelli solari e sistemi ibridi." },
  { icon: Ruler, title: "Ottimizzazione volumi", desc: "Bonus e piani casa: massimizza mq utili e valore commerciale." },
  { icon: Layers, title: "Nuova distribuzione", desc: "Progetti pensati per il mercato attuale: open space, doppi servizi, spazi esterni." },
  { icon: ShieldCheck, title: "Sismico e strutturale", desc: "Miglioramento sismico e certificazioni con strutturisti interni." },
];

export default function Edificazioni() {
  return (
    <div>
      <SEO
        title="Demolizione e ricostruzione a Napoli — Edificazioni ex novo | Ri.Sco."
        description="Demolizione e ricostruzione, edificazioni ex novo e interventi strutturali a Napoli e provincia. Strutturisti e pratiche edilizie interne. Preventivo gratuito con simulatore AI."
        path="/edificazioni"
      />
      <section className="relative min-h-[80vh] flex items-end overflow-hidden -mt-20 pt-20" data-testid="edif-hero">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cantiere di ricostruzione" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-risco-ink/95 via-risco-ink/60 to-risco-ink/30" />
        </div>
        <div className="relative container-x py-20 md:py-28 w-full">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="rule-tag text-stone-300 text-xs uppercase tracking-[0.28em] mb-8">Servizio · 02</div>
              <h1 className="text-risco-bg leading-[0.95]">
                <span className="font-heading font-medium text-4xl sm:text-5xl md:text-6xl lg:text-8xl block tracking-tighter">Edificazioni,</span>
                <span className="font-display italic text-4xl sm:text-5xl md:text-6xl lg:text-8xl block">demolizioni & ricostruzioni</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <p className="text-stone-200 text-base leading-relaxed max-w-md">
                Interveniamo su edifici obsoleti o costruiamo ex novo su terreni edificabili. Strutturisti,
                geometri e ufficio tecnico interno.
              </p>
              <Link
                to="/simulatore"
                data-testid="edif-hero-cta"
                className="mt-8 inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-risco-accent hover:bg-risco-accent-dark text-white font-medium transition-colors"
              >
                Simula il tuo intervento <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="edif-competenze">
        <div className="container-x">
          <div className="overline mb-3">Competenze interne</div>
          <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight max-w-2xl">Tutto quello che serve, sotto un solo tetto</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {[
              "Ingegneri strutturisti (calcoli statici e sismici)",
              "Geometri per rilievi e pratiche catastali",
              "Ufficio tecnico per SCIA, CILA e Permessi di Costruire",
              "Direzione lavori quotidiana e coordinamento sicurezza",
            ].map((t, i) => (
              <div key={t} className="risco-card p-6" data-testid={`edif-comp-${i}`}>
                <div className="text-risco-accent text-sm font-bold">0{i + 1}</div>
                <div className="text-risco-ink mt-2 font-heading">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 border-y border-risco-border bg-risco-alt" data-testid="edif-demo-ricostruzione">
        <div className="container-x">
          <div className="max-w-3xl">
            <div className="overline mb-3">Demolizione + Ricostruzione</div>
            <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight">Un'opportunità, non solo un intervento</h2>
            <p className="text-risco-muted mt-5">
              Ricostruire dove c'era un edificio obsoleto è spesso più conveniente che ristrutturarlo. Guadagni in efficienza
              energetica, ottimizzi i volumi, valorizzi il terreno. Analizziamo il tuo caso e ti mostriamo i numeri.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {benefits.map((b) => (
              <div key={b.title} className="risco-card p-6" data-testid={`edif-benefit-${b.title.toLowerCase().replace(/ /g, "-")}`}>
                <b.icon className="w-6 h-6 text-risco-accent mb-3" />
                <div className="font-heading text-lg text-risco-ink">{b.title}</div>
                <div className="text-sm text-risco-muted mt-2">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="edif-cta">
        <div className="container-x risco-card p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight">Hai un terreno o un immobile da riqualificare?</h2>
            <p className="text-risco-muted mt-4">Ti proponiamo un'analisi di fattibilità gratuita: possibilità edificatorie, stima costi e valore commerciale finale.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/simulatore"
              data-testid="edif-cta-simulatore"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-risco-accent hover:bg-risco-accent-dark text-white font-semibold"
            >
              Preventivo Smart <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/operazioni-immobiliari"
              data-testid="edif-cta-proponi"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md border border-stone-400 hover:border-risco-accent text-risco-ink font-semibold"
            >
              Proponi il tuo terreno
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
